import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { generateLocalizedUrl } from '@/lib/slugUtils';
import { supportedLanguages, type SupportedLanguage } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import Breadcrumb from '@/components/Breadcrumb';
import PageSpecificFAQStructuredData from '@/components/PageSpecificFAQStructuredData';
import { 
  Calculator, 
  TrendingUp,
  Users, 
  Target,
  Clock,
  Sparkles,
  Shield,
  Zap,
  Award,
  ArrowRight,
  Check,
  ChevronRight,
  CheckCircle2,
  BarChart3,
  Database,
  Share2,
  FileText,
  Lightbulb,
  MousePointer,
  Settings,
  Mail,
  Send
} from 'lucide-react';

const howIcons = [MousePointer, Settings, Zap, Mail];
const whyIcons = [Users, TrendingUp, Database];
const strategyIcons = [Target, FileText, Send];
const howToIcons = [Lightbulb, Settings, FileText, Share2, BarChart3];
const whyPriceFormsIcons = [Zap, Award, Share2, Users, Shield];

export default function InteractiveCalculator() {
  const { t, i18n } = useTranslation('interactive-calculator');
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';

  const handleCTAClick = () => {
    const contactUrl = generateLocalizedUrl('contact', currentLang);
    navigate(contactUrl);
  };

  return (
    <>
      <SEOHead
        title={t('meta.title')}
        description={t('meta.description')}
        lang={currentLang}
        path={generateLocalizedUrl('interactive-calculator', currentLang)}
      />
      
      <Header />
      
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/10">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          <Breadcrumb />

          {/* Hero Section */}
          <section className="text-center mb-16 mt-12">
            <Badge variant="secondary" className="mb-6 text-base px-6 py-2" data-testid="badge-hero">
              <Calculator className="w-4 h-4 mr-2 inline" />
              {t('hero.badge')}
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent" data-testid="title-hero">
              {t('hero.title')}
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground mb-4 font-medium" data-testid="text-subtitle">
              {t('hero.subtitle')}
            </p>
            
            <p className="text-lg text-muted-foreground mb-4 max-w-3xl mx-auto" data-testid="text-intro">
              {t('hero.intro')}
            </p>
            
            <p className="text-lg text-muted-foreground mb-4 max-w-3xl mx-auto" data-testid="text-description">
              {t('hero.description')}
            </p>
            
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto" data-testid="text-conclusion">
              {t('hero.conclusion')}
            </p>
            
            <Button 
              size="lg" 
              className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all" 
              onClick={handleCTAClick}
              data-testid="button-cta-hero"
            >
              {t('hero.cta')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </section>

          {/* How It Works Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-how">
                {t('how.title')}
              </h2>
              <p className="text-xl text-muted-foreground" data-testid="text-how-subtitle">
                {t('how.subtitle')}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {Array.isArray(t('how.steps', { returnObjects: true })) && (t('how.steps', { returnObjects: true }) as any[]).map((step: any, index: number) => {
                const Icon = howIcons[index];
                return (
                  <Card key={step.number} className="relative overflow-hidden group hover:shadow-lg transition-shadow" data-testid={`card-how-step-${step.number}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors" data-testid={`icon-how-step-${step.number}`}>
                            <Icon className="w-6 h-6 text-primary" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-xs" data-testid={`badge-step-${step.number}`}>
                              {step.number}
                            </Badge>
                            <h3 className="text-lg font-semibold" data-testid={`title-step-${step.number}`}>
                              {step.title}
                            </h3>
                          </div>
                          <p className="text-muted-foreground" data-testid={`text-step-${step.number}`}>
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Why It Works Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-why">
                {t('why.title')}
              </h2>
              <p className="text-xl text-muted-foreground" data-testid="text-why-subtitle">
                {t('why.subtitle')}
              </p>
            </div>
            
            <div className="space-y-6">
              {Array.isArray(t('why.reasons', { returnObjects: true })) && (t('why.reasons', { returnObjects: true }) as any[]).map((reason: any, index: number) => {
                const Icon = whyIcons[index];
                return (
                  <Card key={index} className="group hover:shadow-lg transition-shadow" data-testid={`card-why-${index}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors" data-testid={`icon-why-${index}`}>
                            <Icon className="w-6 h-6 text-primary" />
                          </div>
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold mb-2" data-testid={`title-why-${index}`}>
                            {reason.title}
                          </h3>
                          <p className="text-muted-foreground" data-testid={`text-why-${index}`}>
                            {reason.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Examples Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-examples">
                {t('examples.title')}
              </h2>
              <p className="text-xl text-muted-foreground" data-testid="text-examples-subtitle">
                {t('examples.subtitle')}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.isArray(t('examples.industries', { returnObjects: true })) && (t('examples.industries', { returnObjects: true }) as any[]).map((industry: any, index: number) => (
                <Card key={index} className="group hover:shadow-lg transition-shadow" data-testid={`card-example-${index}`}>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-2" data-testid={`title-example-${index}`}>
                      {industry.title}
                    </h3>
                    <Badge variant="secondary" className="mb-3" data-testid={`badge-example-type-${index}`}>
                      {industry.type}
                    </Badge>
                    <p className="text-sm text-primary font-medium mb-2 italic" data-testid={`text-example-${index}`}>
                      {industry.example}
                    </p>
                    <p className="text-sm text-muted-foreground" data-testid={`text-example-desc-${index}`}>
                      {industry.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Strategy Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-strategy">
                {t('strategy.title')}
              </h2>
              <p className="text-xl text-muted-foreground" data-testid="text-strategy-subtitle">
                {t('strategy.subtitle')}
              </p>
            </div>
            
            <div className="space-y-6 mb-8">
              {Array.isArray(t('strategy.funnel', { returnObjects: true })) && (t('strategy.funnel', { returnObjects: true }) as any[]).map((stage: any, index: number) => {
                const Icon = strategyIcons[index];
                return (
                  <Card key={index} className="group hover:shadow-lg transition-shadow" data-testid={`card-strategy-${index}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors" data-testid={`icon-strategy-${index}`}>
                            <Icon className="w-6 h-6 text-primary" />
                          </div>
                        </div>
                        <div>
                          <Badge variant="outline" className="mb-2" data-testid={`badge-strategy-${index}`}>
                            {stage.stage}
                          </Badge>
                          <h3 className="text-xl font-semibold mb-2" data-testid={`title-strategy-${index}`}>
                            {stage.title}
                          </h3>
                          <p className="text-muted-foreground" data-testid={`text-strategy-${index}`}>
                            {stage.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            <Card className="bg-primary/5 border-primary/20" data-testid="card-strategy-example">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" data-testid="title-strategy-example">
                  <Sparkles className="w-5 h-5 text-primary" />
                  {t('strategy.example.title')}
                </h3>
                <p className="text-muted-foreground" data-testid="text-strategy-example">
                  {t('strategy.example.description')}
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Case Study Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-case">
                {t('case.title')}
              </h2>
              <p className="text-xl text-muted-foreground" data-testid="text-case-subtitle">
                {t('case.subtitle')}
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {Array.isArray(t('case.results', { returnObjects: true })) && (t('case.results', { returnObjects: true }) as any[]).map((result: any, index: number) => (
                <Card key={index} className="text-center group hover:shadow-lg transition-shadow" data-testid={`card-case-result-${index}`}>
                  <CardContent className="p-6">
                    <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-4" data-testid={`icon-case-${index}`} />
                    <h3 className="text-xl font-bold mb-2 text-primary" data-testid={`title-case-metric-${index}`}>
                      {result.metric}
                    </h3>
                    <p className="text-muted-foreground" data-testid={`text-case-desc-${index}`}>
                      {result.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <Card className="bg-secondary/50" data-testid="card-case-quote">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="text-6xl text-primary/20 leading-none">"</div>
                  <div>
                    <p className="text-lg italic mb-4" data-testid="text-case-quote">
                      {t('case.quote')}
                    </p>
                    <p className="text-sm text-muted-foreground font-medium" data-testid="text-case-author">
                      — {t('case.author')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* How to Get Started Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-howto">
                {t('howTo.title')}
              </h2>
              <p className="text-xl text-muted-foreground" data-testid="text-howto-subtitle">
                {t('howTo.subtitle')}
              </p>
            </div>
            
            <div className="space-y-4">
              {Array.isArray(t('howTo.steps', { returnObjects: true })) && (t('howTo.steps', { returnObjects: true }) as any[]).map((step: any, index: number) => {
                const Icon = howToIcons[index];
                return (
                  <Card key={step.number} className="group hover:shadow-lg transition-shadow" data-testid={`card-howto-${step.number}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-bold" data-testid={`badge-howto-${step.number}`}>
                            {step.number}
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Icon className="w-5 h-5 text-primary" data-testid={`icon-howto-${step.number}`} />
                            <h3 className="text-lg font-semibold" data-testid={`title-howto-${step.number}`}>
                              {step.title}
                            </h3>
                          </div>
                          <p className="text-muted-foreground" data-testid={`text-howto-${step.number}`}>
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Why PriceForms Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-why-priceforms">
                {t('whyPriceForms.title')}
              </h2>
              <p className="text-xl text-muted-foreground" data-testid="text-why-priceforms-subtitle">
                {t('whyPriceForms.subtitle')}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.isArray(t('whyPriceForms.features', { returnObjects: true })) && (t('whyPriceForms.features', { returnObjects: true }) as any[]).map((feature: any, index: number) => {
                const Icon = whyPriceFormsIcons[index];
                return (
                  <Card key={index} className="group hover:shadow-lg transition-shadow" data-testid={`card-priceforms-${index}`}>
                    <CardContent className="p-6">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors" data-testid={`icon-priceforms-${index}`}>
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2" data-testid={`title-priceforms-${index}`}>
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground text-sm" data-testid={`text-priceforms-${index}`}>
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-faq">
                {t('faq.title')}
              </h2>
            </div>
            
            <div className="space-y-6 max-w-3xl mx-auto">
              {Array.isArray(t('faq.questions', { returnObjects: true })) && (t('faq.questions', { returnObjects: true }) as any[]).map((faq: any, index: number) => (
                <Card key={index} className="group hover:shadow-lg transition-shadow" data-testid={`card-faq-${index}`}>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-3 flex items-start gap-2" data-testid={`question-faq-${index}`}>
                      <ChevronRight className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      {faq.question}
                    </h3>
                    <p className="text-muted-foreground pl-7" data-testid={`answer-faq-${index}`}>
                      {faq.answer}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Final CTA Section */}
          <section className="mb-12">
            <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20" data-testid="card-final-cta">
              <CardContent className="p-12 text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-final-cta">
                  {t('cta.title')}
                </h2>
                <p className="text-xl text-muted-foreground mb-6" data-testid="text-final-cta-subtitle">
                  {t('cta.subtitle')}
                </p>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto" data-testid="text-final-cta-description">
                  {t('cta.description')}
                </p>
                
                <div className="flex flex-wrap justify-center gap-4 mb-8">
                  {Array.isArray(t('cta.features', { returnObjects: true })) && (t('cta.features', { returnObjects: true }) as any[]).map((feature: string, index: number) => (
                    <div key={index} className="flex items-center gap-2" data-testid={`feature-cta-${index}`}>
                      <Check className="w-5 h-5 text-primary" />
                      <span className="text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Button 
                  size="lg" 
                  className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all" 
                  onClick={handleCTAClick}
                  data-testid="button-cta-final"
                >
                  {t('cta.button')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </CardContent>
            </Card>
          </section>
        </div>
      </div>
      
      <PageSpecificFAQStructuredData namespace="interactive-calculator" />
      <Footer />
    </>
  );
}
